import * as React from 'react';

import { tv } from '@/utils/styles';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  variant?: 'admin' | 'tracking' | 'simple';
  hasStickyTableHeader?: boolean;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, variant, hasStickyTableHeader, ...props }: TableProps, ref) => {
    const { base, table } = tableStyles({ variant, hasStickyTableHeader });

    return (
      <div className={base()}>
        <table ref={ref} className={table({ className })} {...props} />
      </div>
    );
  },
);

export const tableStyles = tv({
  slots: {
    base: 'relative w-full overflow-auto',
    table: 'w-full table-fixed caption-bottom bg-white text-baseSm leading-normal even:[&_tr]:bg-grey-50',
  },
  variants: {
    variant: {
      admin: {
        // We're only passing the variant to the table slot, so we need
        // to use child selectors to target the other elements
        table:
          'even:[&_tr]:bg-grey-white rounded-md shadow [&_thead]:bg-grey-50 [&_thead]:uppercase [&_thead]:before:absolute [&_thead]:before:bottom-0 [&_thead]:before:left-0 [&_thead]:before:right-0 [&_thead]:before:h-0.5 [&_thead]:before:bg-grey-200 [&_thead_th]:py-4 [&_thead_th]:first:pl-4',
      },
      tracking: {
        table:
          'border-b border-grey-300 [&_td]:h-14 [&_td]:px-2 [&_td]:py-3 first:[&_td]:pl-4 [&_thead]:bg-grey-50 [&_thead]:uppercase [&_thead_th]:border-b-2 [&_thead_th]:border-grey-300 [&_thead_th]:!px-2 [&_thead_th]:px-4 [&_thead_th]:py-5 [&_tr]:border-grey-300 even:[&_tr]:bg-white hover:[&_tr]:bg-grey-50',
      },
      simple: {
        table:
          'w-full table-auto border-b border-grey-300 [&_td]:border [&_td]:border-grey-300 [&_td]:p-0 [&_td]:px-0 [&_thead]:bg-white [&_thead]:uppercase [&_thead]:before:absolute [&_thead]:before:left-0 [&_thead]:before:right-0 [&_thead]:before:top-0 [&_thead]:before:h-px [&_thead]:before:bg-grey-300 [&_thead]:after:absolute [&_thead]:after:bottom-0 [&_thead]:after:left-0 [&_thead]:after:right-0 [&_thead]:after:h-px [&_thead]:after:bg-grey-300 [&_thead_th]:border [&_thead_th]:border-b-0 [&_thead_th]:border-grey-300 [&_thead_th]:px-4 [&_thead_th]:py-5 [&_thead_th]:text-sm [&_thead_th]:font-semibold first:[&_tr>*]:border-l-0 last:[&_tr>*]:border-r-0 [&_tr]:border-grey-300 even:[&_tr]:bg-white hover:[&_tr]:bg-grey-50',
      },
    },
    hasStickyTableHeader: {
      true: {
        table: '[&_thead]:sticky [&_thead]:top-0 [&_thead]:z-2',
      },
    },
  },
});

Table.displayName = 'Table';

export { Table };
